import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useAuth } from '../modules/authentication/AuthProvider';
import { database } from '../lib/supabase';
import HardMatch, { hardMatchSchema } from '../models/HardMatch';

const trustedSites = ['immobilie1', 'immoscout', 'wggesucht', 'immowelt', 'wohnverdient'];

type HardMatchesContextType = {
  hardMatches: HardMatch[];
  isLoading: boolean;
  error: Error | null;
  refreshHardMatches: () => Promise<void>;
  updateHardMatchState: (matchId: number, state: 'hidden' | 'applied' | null) => Promise<void>;
  hardMatchesInProcessing: number;
};

const HardMatchesContext = createContext<HardMatchesContextType | undefined>(undefined);

export const HardMatchesProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [hardMatches, setHardMatches] = useState<HardMatch[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [hardMatchesInProcessing, setHardMatchesInProcessing] = useState<number>(0);
  const { currentUser } = useAuth();

  const fetchHardMatches = async () => {
    if (!currentUser) return;

    setIsLoading(true);
    setError(null);

    try {
      const [{ data, error }] = await Promise.all([
        database.hardMatches.table()
          .select(`
            *,
            flat:flats(*, quality_control(*))
          `)
          .eq('user_id', currentUser.id)
          .eq('flat.is_active', true)
          .not('flat', 'is', null)
          ,
      ]);

      if (error) throw error;

      // Trusted sites: skip quality control and softmatchscore checks
      // Non-trusted: require quality control and softmatchscore >= 6
      const validatedHardMatches = data
        .filter(match => {
          const site = match.flat?.site;
          if (trustedSites.includes(site)) {
            // For trusted sites, we do not check quality control and softmatchscore
            return match.flat?.details_scraped === true;
          }
          // For non-trusted sites, require quality control
          return (
            //match.soft_match_score !== null &&
            match.flat?.quality_control !== null &&
            match.flat?.passed_quality_control === true /*&&
            (match.soft_match_score ?? 0) >= 6*/
          );
        })
        .map(match => {
          const result = hardMatchSchema.safeParse(match);
          if (result.success) {
            return result.data;
          } else {
            console.error('Invalid hard match data:', result.error);
            return null;
          }
        })
        .filter((match): match is HardMatch => match !== null);

      setHardMatches(validatedHardMatches);

      // For matches in processing, keep the old logic for non-trusted, but trusted sites should not need QC/softmatchscore
      let matchesInProcessing = data
        .filter(match => {
          const site = match.flat?.site;
          if (trustedSites.includes(site)) {
            // TRUSTED SITES
            return match.flat?.details_scraped !== true;
          } else {
            // NON-TRUSTED SITES
            return (
              (!match.flat?.quality_control || match.flat?.quality_control.length === 0) ||
              match.flat?.details_scraped !== true
            );
          }
        })

      console.log('Matches in processing:', matchesInProcessing);

      // Filter out all matches older than 10 minutes
      matchesInProcessing = matchesInProcessing
        .filter(match => {
          if (!match.match_timestamp) return false;
          const matchTime = new Date(match.match_timestamp).getTime();
          return matchTime >= Date.now() - 10 * 60 * 1000; // 10 minutes ago
        })
      
      setHardMatchesInProcessing(matchesInProcessing.length);


    } catch (err) {
      console.error('Error fetching hard matches:', err);
      setError(err instanceof Error ? err : new Error('An unknown error occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchHardMatches();
    } else {
      setHardMatches([]);
      setHardMatchesInProcessing(0);
      setIsLoading(false);
      setError(null);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    const subscription = database.hardMatches.getChannel()
      .on('postgres_changes', { event: '*', schema: 'public', table: 'hard_matches' }, (payload) => {
        console.log('Hard matches changed:', payload);
        fetchHardMatches();
      })
      .subscribe(() => {
        console.log('Hard matches subscription created');
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [currentUser]);

  const refreshHardMatches = async () => {
    await fetchHardMatches();
  };

  const updateHardMatchState = async (matchId: number, state: 'hidden' | 'applied' | null) => {
    if (!currentUser) return;

    try {
      console.log('Updating hard match state:', matchId, state, currentUser.id);
      const { error } = await database.hardMatches.table()
        .update({ state })
        .eq('id', matchId)
        .eq('user_id', currentUser.id);

      if (error) throw error;

      // Update local state
      setHardMatches(prevMatches =>
        prevMatches.map(match =>
          match.id === matchId ? { ...match, state } : match
        )
      );
    } catch (err) {
      console.error('Error updating hard match state:', err);
      setError(err instanceof Error ? err : new Error('An unknown error occurred'));
    }
  };

  return (
    <HardMatchesContext.Provider value={{
      hardMatches,
      isLoading,
      error,
      refreshHardMatches,
      updateHardMatchState,
      hardMatchesInProcessing,
    }}>
      {children}
    </HardMatchesContext.Provider>
  );
};

export function useHardMatches(): HardMatchesContextType {
  const context = useContext(HardMatchesContext);
  if (context === undefined) {
    throw new Error('useHardMatches must be used within a HardMatchesProvider');
  }
  return context;
}
